/**
 * Guest Session ✦ — sessionStorage persistence (iter186)
 * ─────────────────────────────────────────────────────────
 * Tab-scoped guest identity persistence. Dies naturally when the
 * tab closes — no TTL, no cross-tab sharing, no localStorage drift.
 *
 * Solves the iter185 trust failure: pressing browser "back" from
 * any guest product (Memory Wall, Lens Notes, etc.) was wiping the
 * guest identity and bouncing the user back to the name-lookup
 * screen — destroying the "stay inside the evening" promise.
 *
 * Contract:
 *   - `saveGuestSession({slug, guest_id, guest_name})` on entry
 *   - `loadGuestSession(slug)` to resume; returns null if missing
 *   - `clearGuestSession(slug)` only on explicit leave/reset
 *   - Keyed by slug so a guest can have multiple events open in
 *     different tabs without cross-contamination.
 */

const KEY = (slug) => `guest_session_${slug}`;

export function saveGuestSession({ slug, guest_id, guest_name, access_token, entry, event_id }) {
  if (!slug || !guest_id) return;
  try {
    sessionStorage.setItem(
      KEY(slug),
      JSON.stringify({
        slug, guest_id,
        guest_name: guest_name || '',
        access_token: access_token || null,
        entry: entry || null,        // iter188 — 'booth_os' | 'access' | null
        event_id: event_id || null,  // iter188 — needed for booth-os routing
        saved_at: Date.now(),
      }),
    );
  } catch { /* private mode / quota — no-op */ }
}

/**
 * iter196 — Touch the existing session WITHOUT clobbering identity.
 * Use this on route enter / product open / scroll restore so the
 * session stays warm with the most-recent activity. Safe to call
 * on every render — no-op when there is no session yet.
 */
export function touchGuestSession(slug, patch = {}) {
  if (!slug) return;
  try {
    const raw = sessionStorage.getItem(KEY(slug));
    if (!raw) return;
    const cur = JSON.parse(raw) || {};
    const next = {
      ...cur,
      ...patch,           // last_route, last_product, etc.
      saved_at: Date.now(),
    };
    sessionStorage.setItem(KEY(slug), JSON.stringify(next));
  } catch { /* no-op */ }
}

export function loadGuestSession(slug) {
  if (!slug) return null;
  try {
    const raw = sessionStorage.getItem(KEY(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.guest_id) return null;
    return parsed;
  } catch { return null; }
}

export function clearGuestSession(slug) {
  if (!slug) return;
  try { sessionStorage.removeItem(KEY(slug)); } catch { /* no-op */ }
}

/**
 * iter189 — resolveDashboardHref(slug)
 * Returns the route every guest product page should send the user
 * to when they tap "Back to Dashboard". iter196 — delegates to the
 * canonical `resolveSmartBackTarget` so both code paths use the
 * single shared priority order:
 *
 *   1. entry='booth_os' + event_id   → /booth-os/:event_id
 *   2. guest_id 'access_*'           → /access/:slug
 *   3. personal-link guest           → /event/:slug/g/:guest_id
 *   4. no session                    → /event/:slug
 */
export function resolveDashboardHref(slug) {
  if (!slug) return '/';
  // Lazy import to avoid the navigation/guestSession circular edge.
  // useSmartBack imports loadGuestSession from this file; calling
  // back at runtime is safe.
  // eslint-disable-next-line global-require
  const { resolveSmartBackTarget } = require('./navigation/useSmartBack');
  return resolveSmartBackTarget({ slug, fallbackHref: `/event/${slug}` });
}
